import '../styles/AdminStageVocabularyReference.css';
import React from 'react';
import { useProjectCollection } from '../lib/projectData';

/* Fallback stage scale — the accepted custody vocabulary. */
const FALLBACK_STAGES = [
  'Picked up',
  'In transit',
  'Customs',
  'Out for delivery',
  'Delivered',
];

const FALLBACK_UNITS = { weightUnit: 'kg', dimensionUnit: 'cm' };

/* Current stage used by the status gauges on the shipment workspace. */
const CURRENT_STAGE_INDEX = 1;

/* Arc tick: a 20px inline SVG that fills proportionally, progress as arc (never a bar). */
const TICK_RADIUS = 7.5;
const TICK_CIRCUMFERENCE = 2 * Math.PI * TICK_RADIUS;

function ArcTick({ index, total, current }) {
  const portion = total > 1 ? (index + 1) / total : 1;
  const dash = TICK_CIRCUMFERENCE * portion;

  return (
    <svg
      className="asvr-tick"
      viewBox="0 0 20 20"
      role="presentation"
      aria-hidden="true"
      focusable="false"
    >
      <circle
        cx="10"
        cy="10"
        r={TICK_RADIUS}
        className="asvr-tick-arc"
        strokeDasharray="0.001 100"
        strokeLinecap="butt"
        opacity="0.28"
        transform="rotate(-90 10 10)"
      />
      <circle
        cx="10"
        cy="10"
        r={TICK_RADIUS}
        className="asvr-tick-arc"
        strokeDasharray={`${dash.toFixed(2)} ${TICK_CIRCUMFERENCE.toFixed(2)}`}
        strokeLinecap="butt"
        transform="rotate(-90 10 10)"
        opacity={index <= current ? 1 : 0.45}
      />
      <circle
        cx="10"
        cy="10"
        r="1.6"
        fill="currentColor"
        opacity="0.9"
      />
    </svg>
  );
}

function AdminStageVocabularyReference() {
  const { data, loading, error } = useProjectCollection('service_configuration');

  const record = Array.isArray(data) && data.length > 0 ? data[0] : null;

  const stages =
    record && Array.isArray(record.stageVocabulary) && record.stageVocabulary.length > 0
      ? record.stageVocabulary
      : FALLBACK_STAGES;

  const weightUnit = (record && record.weightUnit) || FALLBACK_UNITS.weightUnit;
  const dimensionUnit = (record && record.dimensionUnit) || FALLBACK_UNITS.dimensionUnit;

  const serviceName =
    (record && record.serviceName) || 'Fast Forward Deliver Services';
  const updatedAt = record && record.updatedAt ? record.updatedAt : '';
  const total = stages.length;
  const current = Math.min(CURRENT_STAGE_INDEX, Math.max(total - 1, 0));

  return (
    <section
      className="asvr-root"
      aria-labelledby="asvr-title"
    >
      <div className="asvr-inner">
        <header className="asvr-head">
          <div>
            <p className="asvr-kicker">Reference</p>
            <h2 className="asvr-title" id="asvr-title">
              Custody stage vocabulary
            </h2>
          </div>
          <p className="asvr-note">
            Read-only. These are the stage names and default units the status gauges
            read on the shipment workspace and the tracking surface.
          </p>
        </header>

        {loading && (
          <p className="asvr-note" role="status" style={{ paddingTop: '16px' }}>
            Reading saved service configuration…
          </p>
        )}

        {!loading && error && (
          <p className="asvr-note" role="alert" style={{ paddingTop: '16px' }}>
            Saved configuration could not be read. Showing the accepted stage
            vocabulary instead.
          </p>
        )}

        <ol className="asvr-scale" aria-label="Consignment custody stages">
          {stages.map((stage, index) => {
            const isCurrent = index === current;
            return (
              <li
                key={stage}
                className={'asvr-stage' + (isCurrent ? ' is-current' : '')}
                style={{ color: isCurrent ? '#C9A227' : '#8A9299' }}
              >
                <ArcTick index={index} total={total} current={current} />
                <span className="asvr-stage-body">
                  <span className="asvr-stage-label">{stage}</span>
                  <span className="asvr-stage-meta">
                    {String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
                    {isCurrent ? ' · current' : ''}
                  </span>
                </span>
              </li>
            );
          })}
        </ol>

        <div className="asvr-rows">
          <div className="asvr-row">
            <span className="asvr-row-label">Default units</span>
            <div className="asvr-row-value">
              <span className="asvr-units">
                <span className="asvr-unit">
                  <span className="asvr-unit-key">Weight</span>
                  <span className="asvr-unit-val">{weightUnit}</span>
                </span>
                <span className="asvr-unit">
                  <span className="asvr-unit-key">Dimensions</span>
                  <span className="asvr-unit-val">{dimensionUnit}</span>
                </span>
              </span>
            </div>
          </div>

          <div className="asvr-row">
            <span className="asvr-row-label">Takes effect on</span>
            <p className="asvr-row-value">
              Saved configuration applies to the shipment workspace and the tracking
              surface: waybill rows carry these stage names and these units.
            </p>
          </div>

          <div className="asvr-row">
            <span className="asvr-row-label">Service</span>
            <p className="asvr-row-value">
              <span className="asvr-unit-val is-accent">{serviceName}</span>
              {updatedAt ? (
                <span className="asvr-stage-meta">
                  {'  '}· configuration updated {updatedAt}
                </span>
              ) : null}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

export default AdminStageVocabularyReference;